/** @format */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

const isServer = typeof window === "undefined";

class API_CACHE {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pendingRequests: Map<string, PendingRequest<any>> = new Map();

  private formatAgeCache(miliseconds: number): string {
    const seconds = Math.floor(miliseconds / 1000);

    const minutes = Math.floor(seconds / 60);

    const remainingSeconds = seconds / 60;
    if (minutes > 0) {
      return `${minutes} phút ${remainingSeconds} giây`;
    }

    return `${seconds} giây`;
  }

  private formatMaxAgeCache(milliseconds: number): string {
    const minutes = Math.floor(milliseconds / 1000 / 60);
    return `${minutes} phút`;
  }

  get<T>(key: string, maxAge: number): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      //   console.log(`❌ Không tìm thấy Cache cho key : ${key}`);

      return null;
    }

    const age = Date.now() - entry.timestamp;
    const ageFormatted = this.formatAgeCache(age);
    const maxAgeFormatted = this.formatMaxAgeCache(maxAge);

    if (age > maxAge) {
      //   console.log(
      //     `⏰ Cache hết hạn ở key : ${key} (tuổi: ${ageFormatted}, tối đa: ${maxAgeFormatted})`,
      //   );

      this.cache.delete(key);
      return null;
    }

    const remainingTime = this.formatAgeCache(maxAge - age);
    if (isServer) {
      console.log(`✅ Tìm thấy Cache cho Key: ${key}`);
      console.log(
        `   📊 Tuổi: ${ageFormatted} | Còn lại: ${remainingTime} | Tối đa: ${maxAgeFormatted}`,
      );
    }

    return entry.data;
  }

  set<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
    // console.log(`💾 Đã lưu cache cho key: ${key}`);
  }

  async getOrFetch<T>(
    key: string,
    maxAge: number,
    fetchFn: () => Promise<T>,
  ): Promise<T> {
    // 1. Check if we have valid cached data
    const cached = this.get<T>(key, maxAge);
    if (cached !== null) {
      return cached;
    }

    // 2. Check if there's already a pending request for this key
    const pending = this.pendingRequests.get(key);
    if (pending) {
      //   console.log(`⏳ Request đang xử lý, chờ kết quả cho key: ${key}`);
      return pending.promise;
    }

    // 3. No cache and no pending request - create new request
    console.log(`🔄 Bắt đầu fetch mới cho key: ${key}`);

    const promise = (async () => {
      try {
        const result = await fetchFn();
        this.set(key, result);
        return result;
      } catch (error) {
        // Don't cache errors
        console.error(`❌ Lỗi khi fetch cho key: ${key}`, error);
        throw error;
      } finally {
        // Always clean up pending request
        this.pendingRequests.delete(key);
      }
    })();

    // Store the pending request so other callers can wait for it
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  clear(keyPrefix?: string): void {
    if (keyPrefix) {
      // Clear only keys that start with the prefix
      for (const key of this.cache.keys()) {
        if (key.startsWith(keyPrefix)) {
          this.cache.delete(key);
        }
      }
      console.log(`🗑️ Đã xóa cache với prefix: ${keyPrefix}`);
    } else {
      // Clear all cache
      this.cache.clear();
      console.log("🗑️ Đã xóa toàn bộ cache");
    }
  }

  has(key: string, maxAge: number): boolean {
    return this.get(key, maxAge) !== null;
  }

  getStats(): void {
    console.log(`📈 Thống kê Cache: ${this.cache.size} mục được lưu`);
    for (const [key, entry] of this.cache.entries()) {
      const age = Date.now() - entry.timestamp;
      console.log(`   - ${key}: ${this.formatAgeCache(age)}`);
    }
  }
}

export const apiCache = new API_CACHE();

export const CACHE_DURATION = {
  THREE_MINUTES: 3 * 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  TEN_MINUTES: 10 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
} as const;
